import React, { useMemo, useRef, useEffect } from "react";
import { QuestionFragment, AnswerFragment } from '../../../../graphql/generated/definitions';
import useTranslations from "../../../../hooks/useTranslations";

import { Button, VStack } from "@chakra-ui/react";
import { Checkbox, CheckboxGroup, Input, Textarea } from "../../../wombat-ui";

import {
  getAnswersByRole,
  QuestionImplProps,
  QuestionPreviewImplProps,
} from "../Question";
import QuestionTitle from "./utils/QuestionTitle";
import Answer from "./utils/Answer";
import useViewer from "../../../../hooks/useViewer";
import QuestionDocuments from "./utils/QuestionDocuments";

type QuestionSelectorMultipleOptions = {
  value: string;
  label: string;
};

type QuestionSelectorMultipleValue = {
  selector: string[];
  comment: string;
  timeSpent: number;
};

type QuestionSelectorMultiple = {
  options: QuestionSelectorMultipleOptions[];
  defaultValue: QuestionSelectorMultipleValue;
} & QuestionFragment;

export function isQuestionSelectorMultiple(
  q: QuestionFragment
): q is QuestionSelectorMultiple {
  return q.type === "QuestionSelectorMultiple";
}

export function parseQuestion(
  question: QuestionFragment
): QuestionSelectorMultiple {
  return question as QuestionSelectorMultiple;
}

type AnswerSelectorMultiple = {
  value: QuestionSelectorMultipleValue;
} & AnswerFragment;

export function parseAnswers(answers: AnswerFragment[]): AnswerSelectorMultiple[] {
  return answers as AnswerSelectorMultiple[];
}

type QuestionSelectorMultipleProps = QuestionImplProps;

export const QuestionSelectorMultiple: React.FC<QuestionSelectorMultipleProps> =
  React.memo(
    ({
      question: rawQuestion,
      answers: rawAnswers,
      review,
      register,
      errors,
      toggleHistoryModalOpen,
      status,
      displayIndex,
      name,
      needsToBeFilled,
      onReview,
      autoFocus,
    }) => {
      const t = useTranslations();

      const { id: viewerId } = useViewer();
      const question = useMemo(() => parseQuestion(rawQuestion), [rawQuestion]);
      const answers = useMemo(
        () => getAnswersByRole(rawAnswers, parseAnswers, review, viewerId),
        [rawAnswers, viewerId]
      );

      const firstCheckboxRef = useRef(null);
      useEffect(() => {
        if (autoFocus) firstCheckboxRef.current?.focus();
      }, [firstCheckboxRef.current, autoFocus]);

      return (
        <div>
          <CheckboxGroup
            display="row"
            title={
              <QuestionTitle
                displayIndex={displayIndex}
                title={question.label}
                required={question.required && status === "to_be_filled_collaborator"}
                description={question.description}
                referents={
                  question.visibility === "collaborator" ? [] : review.referents
                }
              />
            }
            description={
              <VStack align="flex-start">
                <QuestionDocuments documents={question.documents} />
                <Button
                  onClick={toggleHistoryModalOpen}
                  variant="link"
                  size="xs"
                  id={`answerHistoryButton-${question.id}`}
                >
                  {t("reports.answerHistory")}
                </Button>
              </VStack>
            }
          >
            {question.options.map((opt, index) => (
              <Checkbox
                key={opt.value}
                name={`${name}.value.selector`}
                label={opt.label}
                id={`${name}.value.selector_${opt.value}`}
                value={opt.value}
                disabled={!needsToBeFilled}
                ref={(ref) => {
                  if (index === 0) firstCheckboxRef.current = ref;
                  register(ref, {
                    required: question.required && status === "to_be_filled_collaborator"
                      ? t("forms.errors.required") as string
                      : false,
                  });
                }}
                error={errors}
                variant="boxed"
                defaultChecked={answers?.mine?.[0]?.value?.selector?.includes(
                  opt.value
                )}
              />
            ))}
          </CheckboxGroup>
          {question.timeTracked && (
            <Input
              label={
                !question.expectedTimeValue
                  ? (t("reports.timeSpentInHour") as string)
                  : `${t("reports.timeSpentInHour") as string} - ${t(
                      `reports.expectedTime.${question.expectedTimeUnit}`,
                      {
                        count: question.expectedTimeValue,
                      }
                    )}`
              }
              type="number"
              size="small"
              min={0}
              id={`${question.id}-timeTracked`}
              name={`${name}.timeSpent`}
              disabled={!needsToBeFilled}
              ref={register({ valueAsNumber: true })}
              defaultValue={answers?.collaborator?.[0]?.timeSpent || undefined}
            />
          )}
          {needsToBeFilled && (
            <div className="max-w-2xl pt-4">
              <Textarea
                label={t("reports.comments") as string}
                id={question.id}
                rows={2}
                ref={register()}
                name={`${name}.value.comment`}
                defaultValue={answers?.mine?.[0]?.value?.comment}
                help={t("reports.commentsHelp") as string}
              />
            </div>
          )}
          {(status === "to_be_filled_evaluator" || onReview) && (
            <div className="px-4 py-3 my-2">
              <h4 className="text-sm font-medium leading-5 text-dark-100">
                {t("reports.collaboratorAnswer")}
              </h4>
              <div className="grid grid-cols-1 gap-3 pt-3">
                {answers.collaborator.length > 0 ? (
                  answers.collaborator.map((a) => (
                    <Answer
                      answer={a}
                      withComment
                      comment={a.value.comment}
                      key={a.id}
                    />
                  ))
                ) : (
                  <p className="text-xs">{t("reports.noAnswer")}</p>
                )}
              </div>
            </div>
          )}
          {status !== "to_be_filled_evaluator" && answers.referents.length > 0 && (
            <div className="px-4 py-3 my-2">
              <h4 className="text-sm font-medium leading-5 text-dark-100">
                {t("reports.evaluatorsAnswers", {
                  count: answers.referents.length,
                })}
              </h4>
              <div className="grid grid-cols-1 gap-3 pt-3">
                {answers.referents.length > 0 ? (
                  answers.referents.map((a) => (
                    <Answer
                      answer={a}
                      withComment
                      comment={a.value.comment}
                      key={a.id}
                    />
                  ))
                ) : (
                  <p className="text-xs">{t("reports.noAnswer")}</p>
                )}
              </div>
            </div>
          )}
        </div>
      );
    }
  );

type QuestionSelectorMultiplePreviewProps = QuestionPreviewImplProps;

export const QuestionSelectorMultiplePreview: React.FC<QuestionSelectorMultiplePreviewProps> =
  React.memo(({ question: rawQuestion, displayIndex }) => {
    const t = useTranslations();

    const question = useMemo(() => parseQuestion(rawQuestion), [rawQuestion]);

    return (
      <div>
        <CheckboxGroup
          display="row"
          title={
            <QuestionTitle displayIndex={displayIndex} title={question.label} />
          }
          description={t("reports.multipleSelectorQuestion") as string}
        >
          {question.options.map((opt) => (
            <Checkbox
              key={`${opt.value}`}
              label={opt.label}
              id={`${question.label}_${opt.value}`}
              value={opt.value}
              disabled
              variant="boxed"
            />
          ))}
        </CheckboxGroup>
      </div>
    );
  });
